import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { observer, inject } from 'mobx-react'

import FormModel, { NULL as FormModelNULL } from './model'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import AppModel, { NULL as AppModelNULL } from '@client/components/App/model'
import findAncestor from '@client/utils/findAncestor'
import { defaultSettings, FORM_BUTTON_ALIGN_OPTIONS } from './constants'
import { APP_CONFIG_JSFORMS_GANSUB_BASE_URL } from '@config/app.config'
import {
  WIZARD_TYPE_FORM,
  WIZARD_TYPE_PAGE,
} from '@client/components/Editor/settings'
import { DEFAULT_PADDING } from '@client/components/Block/constants'
import PhoneInput from '@client/components/PhoneInput/PhoneInput'

import inlineStyles from './inlineStyles'

const PADDING = 2 * DEFAULT_PADDING

export class Form extends Component {
  static propTypes = {
    model: PropTypes.instanceOf(FormModel),
    editor: PropTypes.instanceOf(EditorModel),
    app: PropTypes.instanceOf(AppModel),
    serverRender: PropTypes.bool,
  }

  static defaultProps = {
    model: FormModelNULL,
    editor: EditorModelNULL,
    app: AppModelNULL,
    serverRender: false,
  }

  componentDidMount() {
    this.column = findAncestor(this.dom, 'column')
    this.setContainerDimentions()
    if (this.column) {
      this.resizeObserver = new ResizeObserver(() => this.setContainerDimentions())
      this.resizeObserver.observe(this.column)
    }
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }

  calculateStyles = (settings, globalSettings) => {
    const align = settings.button.align || globalSettings.button_simple_align
    const height = settings.button.height || globalSettings.button_height

    const borderStyle =
      settings.button.borderStyle || globalSettings.button_borderStyle
    const borderColor =
      settings.button.borderColor || globalSettings.button_borderColor
    const borderWidth =
      settings.button.borderWidth || globalSettings.button_borderWidth

    return {
      width: align === FORM_BUTTON_ALIGN_OPTIONS[3].value ? '100%' : 'auto',
      minWidth:
        align === FORM_BUTTON_ALIGN_OPTIONS[3].value ? '100%' : undefined,
      lineHeight: typeof height === 'undefined' ? undefined : height + 'px',
      maxHeight: typeof height === 'undefined' ? undefined : 2 * +height + 'px',
      fontSize:
        (settings.button.fontSize || globalSettings.button_fontSize) + 'px',
      border: borderWidth
        ? `${borderWidth}px ${borderStyle} ${borderColor}`
        : 'none',
      borderRadius:
        (settings.button.borderRadius || globalSettings.button_borderRadius) +
        'px',
      background:
        settings.button.backgroundColor ||
        globalSettings.button_backgroundColor,
      color: settings.button.textColor || globalSettings.button_textColor,
    }
  }

  setContainerDimentions() {
    if (!this.column) {
      return
    }
    this.props.model.setContainerDimensions({
      width: this.column.clientWidth - PADDING,
    })
  }

  render() {
    const { model, editor, app, serverRender } = this.props
    const globalSettings = editor.settings.values
    const settings = model.settings.values
    const styles = {
      ...inlineStyles.button,
      ...this.calculateStyles(settings, globalSettings),
      fontFamily: globalSettings.normalText_fontFamily,
    }
    const formatMessage = app.formatMessage

    const text = settings.button.text || defaultSettings.button.text
    const labelColor = settings.labels_color || globalSettings.normalText_color
    const labelFontSize =
      settings.labels_fontSize || globalSettings.normalText_fontSize

    const isForm = app.wizard.type === WIZARD_TYPE_FORM
    const isPage = app.wizard.type === WIZARD_TYPE_PAGE

    const action = isForm
      ? `${APP_CONFIG_JSFORMS_GANSUB_BASE_URL}/webforms/${editor.itemHash}/submit/`
      : editor.url

    return (
      <div
        className="gan-subscribe-form"
        id={isForm ? `gan2_${editor.itemHash}_0` : undefined}
      >
        <form
          ref={(el) => (this.dom = el)}
          style={inlineStyles.form}
          action={action}
          method="POST"
          target={isForm ? '_blank' : undefined}
          data-code={editor.itemHash}
          id={isForm ? 'gan-subscribe-form' : 'gan-popup-form'}
        >
          {settings.fields
            .filter((field) => !field.disabled)
            .map((field, index) => {
              return field.name === 'sms_number' ? (
                <PhoneInput
                  name={isForm ? `fields[${field.name}]` : field.name}
                  key={index}
                  formatMessage={formatMessage}
                  field={field}
                  labelColor={labelColor}
                  globalSettings={globalSettings}
                  labelFontSize={labelFontSize}
                  settings={settings}
                  serverRender={serverRender}
                  inputClassName={
                    isForm || isPage ? undefined : 'gan-phone-field'
                  }
                />
              ) : (
                <div style={{ marginBottom: '10px' }} key={index}>
                  <label
                    style={{
                      color: labelColor,
                      fontSize: `${labelFontSize}px`,
                      lineHeight: globalSettings.normalText_lineHeight,
                      fontFamily: globalSettings.normalText_fontFamily,
                      maxWidth: '250px',
                      whiteSpace: 'nowrap',
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {(field.default || typeof field.default === 'undefined') &&
                    (!field.label || field.label === field.defaultLabel)
                      ? formatMessage(field.defaultLabel)
                      : field.label}
                    {field.required && <span>*</span>}
                  </label>
                  <div>
                    <div
                      className={`gan-validate gan-field-${field.name} ${
                        field.name === 'email' ? 'gan-validate-email' : ''
                      } ${field.required ? 'gan-validate-required' : ''}`}
                    >
                      <input
                        name={isForm ? `fields[${field.name}]` : field.name}
                        type={
                          field.name === 'email'
                            ? 'email'
                            : field.type
                            ? field.type
                            : 'text'
                        }
                        pattern={
                          field.name === 'email'
                            ? '^([a-zA-Z0-9_.+\\-])+@(([a-zA-Z0-9\\-])+.)+([a-zA-Z0-9]){2,40}$'
                            : undefined
                        }
                        required={field.required}
                        style={{
                          width: '100%',
                          padding: '0 10px',
                          height: `${settings.fields_height}px`,
                          lineHeight: `${settings.fields_height}px`,
                          borderWidth: `${settings.fields_borderWidth}px`,
                          borderRadius: `${settings.fields_borderRadius}px`,
                          borderColor: settings.fields_borderColor,
                          borderStyle: 'solid',
                          background: settings.fields_background,
                          color: settings.fields_color,
                          fontSize: `${globalSettings.normalText_fontSize}px`,
                          fontFamily: globalSettings.normalText_fontFamily,
                        }}
                        dir="auto"
                      />
                      <div
                        className="gan-validation-error"
                        style={{
                          color: 'red',
                          fontSize: `${
                            globalSettings.normalText_fontSize * 0.7
                          }px`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          <div
            className={`gan-submit ${
              settings.button.align || globalSettings.button_simple_align
            }`}
            ref={(el) => (this.dom = el)}
          >
            <div
              className="g-recaptcha"
              data-sitekey={isForm ? '##google_captcha_site_key##' : undefined}
              data-callback={isForm ? 'onSubmit' : undefined}
              data-expired-callback={isForm ? 'onExpiredCallback' : undefined}
              data-error-callback={isForm ? 'onErrorCallback' : undefined}
              data-action="submit"
              data-size="invisible"
            />
            <button
              className="btn"
              type="submit"
              style={styles}
              onClick={(ev) => {
                if (!isForm) {
                  return
                }
                ev.preventDefault()
              }}
            >
              <span
                style={{
                  color:
                    settings.button.textColor ||
                    globalSettings.button_textColor,
                  fontWeight:
                    settings.button.fontWeight ||
                    globalSettings.button_fontWeight,
                }}
              >
                {defaultSettings.button.text === text
                  ? formatMessage(defaultSettings.button.text)
                  : text}
              </span>
            </button>
            <div
              className="gan-validation-error gan-validation-form-error"
              style={{
                color: 'red',
                fontSize: `${globalSettings.normalText_fontSize * 0.7}px`,
              }}
            />
          </div>
          <input
            type="hidden"
            name="csrfmiddlewaretoken"
            id={isForm ? 'csrfmiddlewaretoken' : undefined}
            className="csrfmiddlewaretoken"
          />
          <input type="hidden" name="visitor_id" value="##visitor_id##" />
        </form>
      </div>
    )
  }
}

export default inject('editor', 'app', 'serverRender')(observer(Form))
